import React, { useState, useRef } from "react";
import {
  ModalCloseButton,
  ModalBody,
  ModalOverlay,
  Button,
  ModalHeader,
  ModalFooter,
  ModalContent,
  useToast,
} from "@chakra-ui/react";
import { Formik } from "formik";
import TabPrivacy from "./TabPrivacy";
import { SettingsModalContent } from "./Styles";
import { Loading } from "../../../Reusable";
import { MESSAGE } from "../../../Constants/Message";
import {
  useGetProfileQuery,
  useUpdateUserMutation,
} from "../../../../redux/reducers/apiSlice";
import { useEffect } from "react";
import { SpinnerLoading } from "../../../Components/SpinnerLoading/SpinnerLoading";
import * as Sentry from "@sentry/react";
import useScreenType from "react-screentype-hook";
import { IgniteChakraModal } from "../../../Components/Modal/IgniteChakraModal";

type Settings = {
  genderVisibility: boolean;
  emailVisibility: boolean;
  locationVisiblity: boolean;
  pronounsVisibility: boolean;
  phoneVisibility: boolean;
  addressVisibility: boolean;
};

const SettingsModal = ({ isOpen, onClose, userId, ...rest }) => {
  const { isMobile } = useScreenType();
  const { data: currentProfileData, isLoading: currentProfileDataLoading, refetch: refetchProfile } =
    useGetProfileQuery(undefined, { refetchOnMountOrArgChange: true });
  // Force a fresh fetch every time Settings modal opens
  useEffect(() => {
    if (isOpen) {
      refetchProfile();
    }
  }, [isOpen]);
  const [updateProfile, { isLoading: updateProfileLoading }] =
    useUpdateUserMutation();

  const [initialValues, setInitialValues] = useState<Settings>({
    genderVisibility: false,
    emailVisibility: false,
    locationVisiblity: false,
    pronounsVisibility: false,
    phoneVisibility: false,
    addressVisibility: false,
  });

  const toast = useToast();

  const handleFinish = async (preferencesData: Settings) => {
    try {
      await updateProfile({
        preferences: {
          ...preferencesData,
        },
      }).unwrap();

      toast({
        title: "Privacy settings saved successfully",
        position: "bottom-right",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onClose(false);
    } catch (e) {
      Sentry.captureException(e);
      console.error("SettingsUpdateError", e);
      toast({
        title: MESSAGE.SYSTEM_ERROR,
        position: "bottom-right",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const loading =
    updateProfileLoading ||
    currentProfileDataLoading ||
    !currentProfileData?.preferences;

  if (loading) return <SpinnerLoading />;

  return (
    <IgniteChakraModal
      isOpen={isOpen}
      onClose={() => onClose(false)}
      isCentered
      size={isMobile ? "full" : "xl"}
      scrollBehavior="inside"
      {...rest}
    >
      <ModalOverlay />
      <SettingsModalContent
        maxHeight="100%"
        maxWidth="1023px"
        margin="0"
        className="common-modal-content"
      >
        <Loading spin={loading}>
          <Formik
            initialValues={currentProfileData?.preferences || initialValues}
            enableReinitialize
            onSubmit={async (data, handlers) => {}}
          >
            {(formik) => {
              return (
                <>
                  <ModalCloseButton />
                  <ModalHeader>Privacy Settings</ModalHeader>
                  <ModalBody maxHeight="calc(100vh - 179px)">
                    <TabPrivacy formik={formik} />
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      variant="outline"
                      className="btn-outline-dark"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleFinish(formik.values)}
                      colorScheme="primary"
                      loadingText="Save"
                      isLoading={loading}
                      isDisabled={loading}
                      marginLeft="24px"
                      className="gray-for-disabled"
                    >
                      Save
                    </Button>
                  </ModalFooter>
                </>
              );
            }}
          </Formik>
        </Loading>
      </SettingsModalContent>
    </IgniteChakraModal>
  );
};

export default SettingsModal;
